import { inject, Injectable, Injector } from '@angular/core';
import { Client } from '../interface/client';
import { ClientService } from './client.service';
import { Company } from '../interface/company';
import Dexie, { Table, Transaction } from 'dexie';
import moment, { Moment } from 'moment';
import { ClientUtilities } from '../class/client-utilities';
import { Utilities } from '../class/utilities';
import { BehaviorSubject } from 'rxjs';
import { PERSON_STATUS_FILTER } from '../interface/person';

type ClientCursor = string | number;
type ClientDbRecord = Client & {
  searchTokens: string[];
};
type LegacyClientDbRecord = Client & Partial<{
  nameSearch: string;
  fancyNameSearch: string;
  searchTokens: string[];
}>;

@Injectable({
  providedIn: 'root'
})
export class ClientIndexedDbService extends Dexie {
  private readonly _companyIdStorageKey = 'clientIndexedDb.companyId';
  private readonly _lastGetAllStorageKey = 'clientIndexedDb.lastGetAll';
  private readonly _clientsLengthStorageKey = 'clientIndexedDb.clientsLength';
  private readonly _textStopwords = new Set(['de', 'da', 'do', 'das', 'dos', 'a', 'o', 'as', 'os', 'e']);
  private injector = inject(Injector);
  private _clientService!: ClientService;
  private _clientsDb!: Table<ClientDbRecord, string>;
  private _lastGetAll?: Moment;

  syncing$ = new BehaviorSubject<boolean>(false);

  constructor() {
    super('clients');

    this.version(3)
      .stores({
        clients: 'id, document, phone1, phone2, *searchTokens'
      })
      .upgrade(tx => tx.table('clients').toCollection().modify((client: LegacyClientDbRecord) => {
        client.searchTokens = this.buildSearchTokens(client);
        delete client.nameSearch;
        delete client.fancyNameSearch;
      }));

    this.version(4)
      .stores({
        clients: 'id, document, phone1, phone2, *searchTokens'
      })
      .upgrade(tx => tx.table('clients').clear());

    this.version(5)
      .stores({
        clients: 'id, document, phone1, phone2, *searchTokens'
      })
      .upgrade(tx => this.resetClientCache(tx));

    this._clientsDb = this.table<ClientDbRecord, string>('clients');
  }

  private resetClientCache(tx: Transaction) {
    this.clearSyncMetadata();
    return tx.table('clients').clear();
  }

  async localStoreClients(company: Company) {
    const companyKey = this.getCompanyStorageKey(company),
          storedCompanyKey = localStorage.getItem(this._companyIdStorageKey),
          storedLastGetAll = this.getStoredLastGetAll(),
          clientsCount = await this._clientsDb.count();

    if (storedCompanyKey !== companyKey) {
      await this._clientsDb.clear();
      this.clearSyncMetadata();
      await this.fullSyncClients(company, companyKey);
      return;
    }

    const storedClientsLength = this.getStoredClientsLength();
    const hasCompleteLocalCache = clientsCount > 0
      && storedClientsLength !== undefined
      && clientsCount >= storedClientsLength;

    if (clientsCount > 0 && !hasCompleteLocalCache) {
      console.warn(`[IndexedDB] Local clients cache incomplete (${clientsCount}/${storedClientsLength ?? 'unknown'}). Running full sync.`);
      await this._clientsDb.clear();
      this.clearSyncMetadata();
      await this.fullSyncClients(company, companyKey);
      return;
    }

    if (clientsCount && storedLastGetAll) {
      await this.syncUpdatedClients(storedLastGetAll);
      return;
    }

    await this._clientsDb.clear();
    await this.fullSyncClients(company, companyKey);
  }

  private async fullSyncClients(company: Company, companyKey: string) {
    const syncStartedAt = moment();
    this.syncing$.next(true);
    const _startSync = Date.now();
    console.log('[IndexedDB] localStoreClients START');

    try {
      const allClients = await this.clientService.getAll(false, company.cnpjMatrix ?? company.cnpj, true);
      console.log(`[IndexedDB] API returned ${allClients.length} clients (${Date.now() - _startSync}ms)`);

      const validClients = allClients.filter(
        client => !ClientUtilities.isDefault(client.document)
      );
      localStorage.setItem(this._companyIdStorageKey, companyKey);
      localStorage.setItem(this._clientsLengthStorageKey, `${validClients.length}`);

      // Insert in chunks to avoid blocking the UI thread
      const CHUNK_SIZE = 2000;
      let inserted = 0;
      for (let i = 0; i < validClients.length; i += CHUNK_SIZE) {
        const chunk = validClients.slice(i, i + CHUNK_SIZE).map(client => this.normalizeForDb(client));
        try {
          await this._clientsDb.bulkPut(chunk);
          inserted += chunk.length;
        } catch (error) {
          console.warn(`[IndexedDB] Failed to insert chunk at index ${i}`, error);
        }
      }

      if (inserted < validClients.length) {
        throw new Error(`[IndexedDB] Incomplete client sync: inserted ${inserted} of ${validClients.length}`);
      }

      console.log(`[IndexedDB] Inserted ${inserted} clients`);
      this.lastGetAll = syncStartedAt;
    } catch (error) {
      console.error(error);
    } finally {
      this.syncing$.next(false);
      console.log(`[IndexedDB] localStoreClients END (${Date.now() - _startSync}ms)`);
    }
  }

  private async syncUpdatedClients(lastGetAll: Moment) {
    const syncStartedAt = moment();
    this.syncing$.next(true);
    const _startSync = Date.now();
    console.log('[IndexedDB] syncUpdatedClients START');

    try {
      const clientsUpdated = await this.clientService.getUpdateAfterDate(lastGetAll.toISOString());

      for (const clientUpdated of clientsUpdated) {
        await this.updateClient(clientUpdated);
      }

      this.lastGetAll = syncStartedAt;
      console.log(`[IndexedDB] Updated ${clientsUpdated.length} clients`);
    } catch (error) {
      console.error(error);
    } finally {
      this.syncing$.next(false);
      console.log(`[IndexedDB] syncUpdatedClients END (${Date.now() - _startSync}ms)`);
    }
  }

  private get clientService(): ClientService {
    if (!this._clientService) {
      this._clientService = this.injector.get(ClientService);
    }
    return this._clientService;
  }

  private normalizeSearchText(text: string): string {
    const normalized = Utilities.removeDiacritics(text || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').trim();
    return normalized.replace(/\s+/g, ' ');
  }

  private normalizeMaskToken(value: string | number | undefined): string {
    return Utilities.removeDiacritics(`${value || ''}`).toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private getCompanyStorageKey(company: Company): string {
    return company.id || this.normalizeMaskToken(company.cnpj);
  }

  private getStoredLastGetAll(): Moment | undefined {
    const stored = localStorage.getItem(this._lastGetAllStorageKey);
    if (!stored) {
      return undefined;
    }

    const date = moment(stored);
    return date.isValid() ? date : undefined;
  }

  private getStoredClientsLength(): number | undefined {
    const storedValue = localStorage.getItem(this._clientsLengthStorageKey);
    if (storedValue === null) {
      return undefined;
    }

    const stored = Number(storedValue);
    return Number.isFinite(stored) ? stored : undefined;
  }

  private clearSyncMetadata() {
    localStorage.removeItem(this._lastGetAllStorageKey);
    localStorage.removeItem(this._clientsLengthStorageKey);
    this._lastGetAll = undefined;
  }

  private textWords(text: string): string[] {
    return this.normalizeSearchText(text)
      .split(' ')
      .filter(word => word && !this._textStopwords.has(word));
  }

  private prefixTokens(value: string): string[] {
    const tokens: string[] = [];
    for (let i = 1; i <= value.length; i++) {
      tokens.push(value.slice(0, i));
    }
    return tokens;
  }

  private buildSearchTokens(client: Client): string[] {
    const tokens = new Set<string>();

    for (const word of [...this.textWords(client.name || ''), ...this.textWords(client.fancyName || ''), ...this.textWords(client.nickname || '')]) {
      this.prefixTokens(word).forEach(token => tokens.add(token));
    }

    for (const value of [client.document, client.phone1, client.phone2]) {
      const normalized = this.normalizeMaskToken(value);
      if (normalized) {
        this.prefixTokens(normalized).forEach(token => tokens.add(token));
      }
    }

    return Array.from(tokens);
  }

  private searchTokensFromInput(text: string): string[] {
    const rawText = text || '';
    const hasDigit = /\d/.test(rawText);
    const hasLetter = /[A-Za-zÀ-ÿ]/.test(rawText);

    if (hasDigit && !hasLetter) {
      const token = this.normalizeMaskToken(rawText);
      return token ? [token] : [];
    }

    const tokens = new Set(this.textWords(rawText));

    if (hasDigit) {
      for (const chunk of rawText.split(/\s+/)) {
        if (/\d/.test(chunk)) {
          const token = this.normalizeMaskToken(chunk);
          if (token) {
            tokens.add(token);
          }
        }
      }
    }

    return Array.from(tokens);
  }

  /** The local counterpart of the API's `activeStatus` filter. */
  private matchesStatusFilter(client: Client, statusFilter: PERSON_STATUS_FILTER): boolean {
    switch (statusFilter) {
      case PERSON_STATUS_FILTER.ALL:
        return true;
      case PERSON_STATUS_FILTER.INACTIVE:
        return client.active === 0;
      default:
        return client.active !== 0;
    }
  }

  private dedupeClients(clients: Client[]): Client[] {
    const map = new Map<string, Client>();
    clients.forEach(client => {
      if (client?.id && !map.has(client.id)) {
        map.set(client.id, client);
      }
    });
    return Array.from(map.values());
  }

  private normalizeForDb(client: Client): ClientDbRecord {
    return {
      ...client,
      searchTokens: this.buildSearchTokens(client)
    };
  }

  private async searchByTokens(tokens: string[], limit?: number, pageIndex = 0, statusFilter = PERSON_STATUS_FILTER.ACTIVE): Promise<{ clients: Client[], hasNext: boolean }> {
    if (!tokens.length) {
      return { clients: [], hasNext: false };
    }

    const results = await Promise.all(tokens.map(token => this._clientsDb.where('searchTokens').equals(token).toArray()));
    if (results.some(result => result.length === 0)) {
      return { clients: [], hasNext: false };
    }

    const clientsById = new Map<string, ClientDbRecord>();
    this.dedupeClients(results[0]).forEach(client => clientsById.set(client.id, client as ClientDbRecord));

    for (const result of results.slice(1)) {
      const ids = new Set(result.map(client => client.id));
      Array.from(clientsById.keys()).forEach(id => {
        if (!ids.has(id)) {
          clientsById.delete(id);
        }
      });
    }

    const merged = Array.from(clientsById.values()).filter(client => this.matchesStatusFilter(client, statusFilter));
    const start = limit ? pageIndex * limit : 0;
    const end = limit ? start + limit : undefined;
    const hasNext = limit ? merged.length > start + limit : false;
    const paged = limit ? merged.slice(start, end) : merged;
    return { clients: paged, hasNext };
  }

  async addClient(client: Client) {
    const isDefaltClient = ClientUtilities.isDefault(client.document);

    if (!isDefaltClient) {
      await this._clientsDb.put(this.normalizeForDb(client));
    } else {
      await this._clientsDb.delete(client.id);
    };
  }

  async updateClient(client: Client) {
    const isDefaltClient = ClientUtilities.isDefault(client.document);

    if (!isDefaltClient) {
      await this._clientsDb.put(this.normalizeForDb(client));
    } else {
      await this._clientsDb.delete(client.id);
    };
  }

  async getById(id: string) {
    return await this._clientsDb.get(id);
  }

  async searchForUsers(text: string, statusFilter = PERSON_STATUS_FILTER.ACTIVE): Promise<Client[]> {
    const searchTokens = this.searchTokensFromInput(text);
    if (!this.normalizeSearchText(text) && !this.normalizeMaskToken(text)) {
      return await this._clientsDb.orderBy('id')
        .filter(client => this.matchesStatusFilter(client, statusFilter))
        .limit(20).toArray();
    }

    return (await this.searchByTokens(searchTokens, 50, 0, statusFilter)).clients;
  }

  private toCursor(client: Client): ClientCursor {
    return client.id;
  }

  async countSearchResults(text: string, statusFilter = PERSON_STATUS_FILTER.ACTIVE): Promise<number> {
    const searchTokens = this.searchTokensFromInput(text);
    if (!this.normalizeSearchText(text) && !this.normalizeMaskToken(text)) {
      return await this._clientsDb.filter(client => this.matchesStatusFilter(client, statusFilter)).count();
    }

    const { clients } = await this.searchByTokens(searchTokens, undefined, 0, statusFilter);
    return clients.length;
  }

  private async searchPaginatedByCursor(searchTokens: string[], isEmptySearch: boolean, limit: number, after?: ClientCursor, statusFilter = PERSON_STATUS_FILTER.ACTIVE): Promise<{ clients: Client[], nextCursor?: ClientCursor, hasNext: boolean }> {
    if (isEmptySearch) {
      const collection = after
        ? this._clientsDb.where('id').above(after as string)
        : this._clientsDb.orderBy('id');

      const pageItems = await collection
        .filter(client => this.matchesStatusFilter(client, statusFilter))
        .limit(limit + 1).toArray();
      const hasNext = pageItems.length > limit;
      if (hasNext) {
        pageItems.pop();
      }

      const nextCursor = pageItems.length > 0 ? this.toCursor(pageItems[pageItems.length - 1]) : undefined;
      return { clients: pageItems, nextCursor, hasNext };
    }

    const pageIndex = typeof after === 'number' ? after : 0;
    const { clients, hasNext } = await this.searchByTokens(searchTokens, limit, pageIndex, statusFilter);
    const nextCursor = hasNext ? pageIndex + 1 : undefined;

    return { clients, nextCursor, hasNext };
  }

  set lastGetAll(date: Moment) {
    this._lastGetAll = date;
    localStorage.setItem(this._lastGetAllStorageKey, date.toISOString());
  }

  get lastGetAll() {
    if (!this._lastGetAll) {
      this._lastGetAll = this.getStoredLastGetAll() || moment(0);
    }
    return this._lastGetAll;
  }

  async searchPaginated(text: string, limit: number, after?: ClientCursor, statusFilter = PERSON_STATUS_FILTER.ACTIVE): Promise<{ clients: Client[], nextCursor?: ClientCursor, hasNext: boolean }> {
    const _start = Date.now();
    const searchTokens = this.searchTokensFromInput(text);
    const isEmptySearch = !this.normalizeSearchText(text) && !this.normalizeMaskToken(text);
    const result = await this.searchPaginatedByCursor(searchTokens, isEmptySearch, limit, after, statusFilter);
    console.log(`[IndexedDB] searchPaginated('${text}', ${limit}, ${after ?? 'start'}) -> ${result.clients.length} (${Date.now() - _start}ms)`);
    return result;
  }
}
